import type { WidgetManifest } from '@resto/contracts';

/** Поля, которые подписывает ядро: всё, что влияет на загрузку кода виджета. */
const SIGNED_FIELDS = [
  'id',
  'version',
  'entryUrl',
  'moduleUrl',
  'checksum',
  'minHostVersion',
  'channels',
] as const;

function sorted(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sorted);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => [key, sorted((value as Record<string, unknown>)[key])]),
    );
  }
  return value;
}

export function manifestPayload(manifest: WidgetManifest): Uint8Array<ArrayBuffer> {
  const data: Record<string, unknown> = {};
  for (const field of SIGNED_FIELDS) {
    data[field] = (manifest as Record<string, unknown>)[field] ?? null;
  }
  return new Uint8Array(new TextEncoder().encode(JSON.stringify(sorted(data))));
}

function pemToBytes(pem: string): Uint8Array<ArrayBuffer> {
  const base64 = pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/**
 * Проверка подписи манифеста. Без верной подписи виджет не монтируется:
 * подмена entryUrl в ответе реестра не должна приводить к загрузке чужого кода.
 */
export async function verifyManifestSignature(
  manifest: WidgetManifest,
  publicKeyPem: string,
  keyId?: string,
): Promise<boolean> {
  if (!manifest.signature || !publicKeyPem) return false;
  if (keyId && manifest.keyId && manifest.keyId !== keyId) return false;
  const key = await crypto.subtle.importKey(
    'spki',
    pemToBytes(publicKeyPem),
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['verify'],
  );
  const raw = atob(manifest.signature);
  const signatureBytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) {
    signatureBytes[index] = raw.charCodeAt(index);
  }
  return crypto.subtle.verify(
    { name: 'RSASSA-PKCS1-v1_5' },
    key,
    signatureBytes,
    manifestPayload(manifest),
  );
}
